import React from "react"
import { useEffect, useState } from "react"
import { Helmet } from "react-helmet"
import type { CSSProperties, ReactNode } from "react"

const styles = `
  @import url('https://fonts.googleapis.com/css2?family=Lora:ital,wght@0,400;0,500;1,400&family=DM+Mono:wght@300;400&display=swap');

  *, *::before, *::after {
    box-sizing: border-box;
    margin: 0;
    padding: 0;
  }

  :root {
    --sand: #f5f0e8;
    --sand-dark: #e8e0d0;
    --ink: #2a2520;
    --ink-light: #6b5f52;
    --accent: #c4744a;
    --accent-light: #e8c4a8;
    --warm-white: #faf7f2;
  }

  html { scroll-behavior: smooth; }

  a {
    color: inherit;
    text-decoration: none;
  }

  a:hover,
  a:focus-visible {
    text-decoration: underline;
  }

  body {
    background-color: var(--sand);
    color: var(--ink);
    font-family: 'Lora', Georgia, serif;
    min-height: 100vh;
    overflow-x: hidden;
  }

  @keyframes fadeUp {
    from { opacity: 0; transform: translateY(18px); }
    to   { opacity: 1; transform: translateY(0); }
  }

  @keyframes fadeIn {
    from { opacity: 0; }
    to   { opacity: 1; }
  }

  .animate-fade-up   { animation: fadeUp 0.7s ease forwards; }
  .animate-fade-in   { animation: fadeIn 1s  ease forwards; }
  .opacity-0         { opacity: 0; }

  .grain {
    position: fixed;
    inset: 0;
    pointer-events: none;
    z-index: 100;
    opacity: 0.035;
    background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E");
    background-size: 200px 200px;
  }
`

type HoverStyleFn = (hovered: boolean) => CSSProperties

const s: {
  page: CSSProperties
  nav: CSSProperties
  navMono: CSSProperties
  navDot: CSSProperties
  main: CSSProperties
  heroEyebrow: CSSProperties
  heroName: CSSProperties
  heroNameEm: CSSProperties
  heroTagline: CSSProperties
  divider: CSSProperties
  sectionLabel: CSSProperties
  serviceGrid: CSSProperties
  serviceItem: HoverStyleFn
  serviceNum: CSSProperties
  serviceTitle: CSSProperties
  serviceDesc: CSSProperties
  contactInner: CSSProperties
  contactHeadline: CSSProperties
  contactHeadlineEm: CSSProperties
  contactSub: CSSProperties
  contactLink: HoverStyleFn
  linkIconEmail: CSSProperties
  linkIconLinkedIn: CSSProperties
  linkLabel: CSSProperties
  linkValue: CSSProperties
  footer: CSSProperties
  footerMono: CSSProperties
} = {
  page: {
    backgroundColor: "var(--sand)",
    color: "var(--ink)",
    fontFamily: "'Lora', Georgia, serif",
    minHeight: "100vh",
    overflowX: "hidden",
  },
  nav: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "2.2rem 2rem",
    maxWidth: 900,
    margin: "0 auto",
    position: "relative",
  },
  navMono: {
    fontFamily: "'DM Mono', monospace",
    fontSize: 11,
    fontWeight: 300,
    letterSpacing: "0.12em",
    color: "var(--ink-light)",
    textTransform: "uppercase",
  },
  navDot: {
    width: 6,
    height: 6,
    background: "var(--accent)",
    borderRadius: "50%",
    position: "absolute", // 👈 key
    left: "50%", // 👈 center horizontally
    transform: "translateX(-50%)", // 👈 perfect centering
  },
  main: {
    maxWidth: 900,
    margin: "0 auto",
    padding: "0 2rem",
  },
  heroEyebrow: {
    fontFamily: "'DM Mono', monospace",
    fontSize: 11,
    fontWeight: 300,
    letterSpacing: "0.2em",
    color: "var(--accent)",
    textTransform: "uppercase",
    marginBottom: "1.4rem",
  },
  heroName: {
    fontSize: "clamp(3rem, 7vw, 5.5rem)",
    fontWeight: 400,
    lineHeight: 1.05,
    letterSpacing: "-0.02em",
    color: "var(--ink)",
    marginBottom: "0.3rem",
  },
  heroNameEm: {
    fontStyle: "italic",
    color: "var(--accent)",
  },
  heroTagline: {
    fontSize: "1.05rem",
    color: "var(--ink-light)",
    lineHeight: 1.7,
    maxWidth: 480,
    marginTop: "1.8rem",
  },
  divider: {
    height: 1,
    background: "linear-gradient(to right, var(--accent-light), transparent)",
    margin: "3rem 0",
  },
  sectionLabel: {
    fontFamily: "'DM Mono', monospace",
    fontSize: 10,
    fontWeight: 300,
    letterSpacing: "0.2em",
    textTransform: "uppercase",
    color: "var(--ink-light)",
    marginBottom: "1rem",
  },
  serviceGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
    gap: 1,
    background: "var(--sand-dark)",
    border: "1px solid var(--sand-dark)",
    borderRadius: 4,
    overflow: "hidden",
    marginTop: "1.5rem",
  },
  serviceItem: (hovered: boolean): CSSProperties => ({
    background: hovered ? "var(--sand)" : "var(--warm-white)",
    padding: "1.4rem 1.4rem 1.6rem",
    transition: "background 0.2s",
    cursor: "default",
  }),
  serviceNum: {
    fontFamily: "'DM Mono', monospace",
    fontSize: 10,
    color: "var(--accent)",
    letterSpacing: "0.1em",
    marginBottom: "0.7rem",
  },
  serviceTitle: {
    fontSize: "0.95rem",
    fontWeight: 500,
    color: "var(--ink)",
    marginBottom: "0.4rem",
  },
  serviceDesc: {
    fontSize: "0.82rem",
    color: "var(--ink-light)",
    lineHeight: 1.6,
  },
  contactInner: {
    background: "var(--ink)",
    borderRadius: 6,
    padding: "2.8rem 3rem",
  },
  contactHeadline: {
    fontSize: "1.7rem",
    fontWeight: 400,
    color: "var(--warm-white)",
    lineHeight: 1.25,
  },
  contactHeadlineEm: {
    fontStyle: "italic",
    color: "var(--accent-light)",
  },
  contactSub: {
    fontFamily: "'DM Mono', monospace",
    fontSize: "0.85rem",
    fontWeight: 300,
    letterSpacing: "0.04em",
    color: "#9e9080",
    marginTop: "0.7rem",
    lineHeight: 1.6,
  },
  contactLink: (hovered: boolean): CSSProperties => ({
    display: "flex",
    alignItems: "center",
    gap: "1rem",
    textDecoration: "none",
    padding: "0.85rem 1.1rem",
    borderRadius: 4,
    border: `1px solid ${
      hovered ? "rgba(255,255,255,0.2)" : "rgba(255,255,255,0.1)"
    }`,
    background: hovered ? "rgba(255,255,255,0.09)" : "rgba(255,255,255,0.04)",
    transition: "background 0.2s, border-color 0.2s",
    color: "var(--warm-white)",
  }),
  linkIconEmail: {
    width: 32,
    height: 32,
    borderRadius: "50%",
    background: "var(--accent)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },
  linkIconLinkedIn: {
    width: 32,
    height: 32,
    borderRadius: "50%",
    background: "#0a66c2",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },
  linkLabel: {
    fontFamily: "'DM Mono', monospace",
    fontSize: "0.78rem",
    letterSpacing: "0.08em",
    color: "#9e9080",
    textTransform: "uppercase",
    display: "block",
    marginBottom: 1,
  },
  linkValue: {
    fontSize: "0.9rem",
    color: "var(--warm-white)",
  },
  footer: {
    borderTop: "1px solid var(--sand-dark)",
    padding: "1.8rem 2rem",
    maxWidth: 900,
    margin: "0 auto",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  footerMono: {
    fontFamily: "'DM Mono', monospace",
    fontSize: 10,
    fontWeight: 300,
    color: "var(--ink-light)",
    letterSpacing: "0.1em",
  },
}

/* ─── Icons ─── */
const EmailIcon = (): JSX.Element => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="white">
    <path d="M20 4H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4-8 5-8-5V6l8 5 8-5v2z" />
  </svg>
)

const LinkedInIcon = (): JSX.Element => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="white">
    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 0 1-2.063-2.065 2.064 2.064 0 1 1 2.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
  </svg>
)

/* ─── Sub-components ─── */
type Service = {
  num: string
  title: string
  desc: string
}

const services: Service[] = [
  {
    num: "01",
    title: "Business Analysis",
    desc:
      "Translating business needs into clear requirements, user stories, and delivery-ready scope.",
  },
  {
    num: "02",
    title: "Solution Design",
    desc:
      "Bridging business and tech to shape solutions that are practical, scalable, and grounded in reality.",
  },
  {
    num: "03",
    title: "Delivery Support",
    desc:
      "Supporting software projects from concept to rollout through alignment, clarification, and structured execution.",
  },
  {
    num: "04",
    title: "AI Solution Engineering",
    desc:
      "Identifying where AI creates real value and helping integrate it into products, workflows, and business processes.",
  },
]

type ServiceItemProps = {
  num: string
  title: string
  desc: string
}

function ServiceItem({ num, title, desc }: ServiceItemProps): JSX.Element {
  const [hovered, setHovered] = useState<boolean>(false)

  return (
    <div
      style={s.serviceItem(hovered)}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <p style={s.serviceNum}>{num}</p>
      <p style={s.serviceTitle}>{title}</p>
      <p style={s.serviceDesc}>{desc}</p>
    </div>
  )
}

type ContactLinkProps = {
  href: string
  iconStyle: CSSProperties
  icon: ReactNode
  label: string
  value: string
}

function ContactLink({
  href,
  iconStyle,
  icon,
  label,
  value,
}: ContactLinkProps): JSX.Element {
  const [hovered, setHovered] = useState<boolean>(false)

  return (
    <a
      href={href}
      style={s.contactLink(hovered)}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      target={href.startsWith("http") ? "_blank" : undefined}
      rel={href.startsWith("http") ? "noopener noreferrer" : undefined}
    >
      <div style={iconStyle}>{icon}</div>
      <div>
        <span style={s.linkLabel}>{label}</span>
        <span style={s.linkValue}>{value}</span>
      </div>
    </a>
  )
}

/* ─── Responsive hook ─── */
function useIsMobile(): boolean {
  const [isMobile, setIsMobile] = useState<boolean>(
    typeof window !== "undefined" ? window.innerWidth < 640 : false
  )

  useEffect(() => {
    const handler = (): void => setIsMobile(window.innerWidth < 640)
    window.addEventListener("resize", handler)
    return () => window.removeEventListener("resize", handler)
  }, [])

  return isMobile
}

/* ─── Main component ─── */
export default function HomePage(): JSX.Element {
  const isMobile = useIsMobile()

  const delays: string[] = [
    "0.1s",
    "0.2s",
    "0.35s",
    "0.5s",
    "0.55s",
    "0.7s",
    "0.85s",
  ]

  return (
    <div style={s.page}>
      <Helmet>
        <title>Hendrik Gruber | Business Analyst & AI Solution Engineer</title>
        <meta
          name="description"
          content="Business analyst and AI solution engineer focused on software and technology projects."
        />
      </Helmet>
      <style>{styles}</style>

      <div className="grain" />

      <nav style={s.nav}>
        <span style={s.navMono}>
          <a href="/">Home</a>
        </span>
        <div style={s.navDot} />
        <span style={s.navMono}>
          <a href="/imprint">Imprint</a>
        </span>
      </nav>

      <main style={s.main}>
        <section
          style={{ padding: isMobile ? "3rem 0 2.5rem" : "5rem 0 4rem" }}
        >
          <p
            className="opacity-0 animate-fade-up"
            style={{ ...s.heroEyebrow, animationDelay: delays[0] }}
          >
            Hello,
          </p>
          <h1
            className="opacity-0 animate-fade-up"
            style={{ ...s.heroName, animationDelay: delays[1] }}
          >
            I'm <em style={s.heroNameEm}>Hendrik</em>
          </h1>
          <p
            className="opacity-0 animate-fade-up"
            style={{ ...s.heroTagline, animationDelay: delays[2] }}
          >
            I help software and technology projects move from complexity to
            clarity — bridging the gap between what teams build and what
            businesses actually need.
          </p>
        </section>

        <div
          className="opacity-0 animate-fade-in"
          style={{ ...s.divider, animationDelay: delays[3] }}
        />

        <section
          className="opacity-0 animate-fade-up"
          style={{
            display: "grid",
            gridTemplateColumns: isMobile ? "1fr" : "1fr 1.6fr",
            gap: isMobile ? "1.2rem" : "3rem",
            alignItems: "start",
            paddingBottom: "4rem",
            animationDelay: delays[4],
          }}
        >
          <p style={s.sectionLabel}>About</p>
          <div
            style={{ fontSize: "1rem", lineHeight: 1.8, color: "var(--ink)" }}
          >
            <p>
              I'm a business analyst specialising in software and technology
              implementation projects. My work lives at the intersection of
              people, processes, and products — translating requirements into
              something a development team can actually deliver.
            </p>
            <p style={{ marginTop: "1rem" }}>
              My focus is on turning business needs into solutions that are
              actually usable, scalable, and technically realistic. Alongside
              classic business analysis, I also work in AI-related roles —
              helping teams identify where AI adds real value and how it can be
              integrated into software products in a practical way.
            </p>
          </div>
        </section>

        <section
          className="opacity-0 animate-fade-up"
          style={{ paddingBottom: "5rem", animationDelay: delays[5] }}
        >
          <p style={s.sectionLabel}>What I do</p>
          <div style={s.serviceGrid}>
            {services.map((svc: Service) => (
              <ServiceItem key={svc.num} {...svc} />
            ))}
          </div>
        </section>

        <section
          className="opacity-0 animate-fade-up"
          style={{ paddingBottom: "6rem", animationDelay: delays[6] }}
        >
          <div
            style={{
              ...s.contactInner,
              display: "grid",
              gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
              gap: "2rem",
              alignItems: "center",
              padding: isMobile ? "2rem" : "2.8rem 3rem",
            }}
          >
            <div>
              <h2 style={s.contactHeadline}>
                Let's talk about{" "}
                <em style={s.contactHeadlineEm}>your project.</em>
              </h2>
              <p style={s.contactSub}>
                Feel free to reach out. A short conversation is usually all it
                takes to see if there's a fit.
              </p>
            </div>
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "0.9rem",
              }}
            >
              <ContactLink
                href="mailto:gruber.hendrik@gmail.com"
                iconStyle={s.linkIconEmail}
                icon={<EmailIcon />}
                label="Email"
                value="gruber.hendrik@gmail.com"
              />
              <ContactLink
                href="https://www.linkedin.com/in/hendrik-gruber/"
                iconStyle={s.linkIconLinkedIn}
                icon={<LinkedInIcon />}
                label="LinkedIn"
                value="linkedin.com/in/hendrik-gruber"
              />
            </div>
          </div>
        </section>
      </main>

      <footer style={s.footer}>
        <span style={s.footerMono}>
          © {new Date().getFullYear()} Hendrik Gruber
        </span>
        <span style={s.footerMono}>Frankfurt am Main, DE</span>
        <span>
          <a href="/privacy" style={s.footerMono}>
            Privacy Policy
          </a>
        </span>
      </footer>
    </div>
  )
}
